<template>
  <div v-if="visible" class="modal-overlay" @click.self="$emit('close')">
    <div class="gm-panel gm-panel--raised library-modal">
      <div class="modal-header">
        <h2 class="gm-title modal-title">Battle Map Library</h2>
        <button class="close-btn" @click="$emit('close')">×</button>
      </div>

      <!-- Filters -->
      <div class="filters-section">
        <input
          v-model="searchQuery"
          type="text"
          class="gm-input search-input"
          placeholder="Search maps..."
          @input="handleSearchChange"
        />
        <select v-model="selectedCategory" class="gm-select filter-select" @change="loadTemplates">
          <option value="">All Categories</option>
          <option value="dungeon">Dungeon</option>
          <option value="urban">Urban</option>
          <option value="wilderness">Wilderness</option>
          <option value="custom">Custom</option>
        </select>
        <select v-model="selectedDifficulty" class="gm-select filter-select" @change="loadTemplates">
          <option value="">All Difficulties</option>
          <option value="easy">Easy</option>
          <option value="medium">Medium</option>
          <option value="hard">Hard</option>
        </select>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="gm-empty loading-state">
        <span class="dx-loader" aria-hidden="true"></span>
        <p class="gm-empty__hint">Loading templates...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="gm-empty error-state">
        <p class="error-message">{{ error }}</p>
        <button class="gm-btn retry-btn" @click="loadTemplates">Retry</button>
      </div>

      <!-- Templates Grid -->
      <div v-else-if="templates.length > 0" class="templates-grid gm-scroll">
        <div
          v-for="template in templates"
          :key="template.id"
          class="template-card"
          :class="{ selected: selectedTemplate?.id === template.id }"
          @click="selectTemplate(template)"
        >
          <div class="template-thumbnail">
            <img
              v-if="template.thumbnail"
              :src="template.thumbnail"
              :alt="template.name"
              class="thumbnail-img"
            />
            <div v-else class="thumbnail-placeholder">MAP</div>
          </div>
          <div class="template-info">
            <div class="template-header">
              <h3 class="template-name">{{ template.name }}</h3>
              <button
                class="gm-btn gm-btn--sm gm-btn--danger delete-btn"
                @click.stop="handleDeleteTemplate(template)"
                title="Delete template"
              >
                Delete
              </button>
            </div>
            <p class="template-author">by {{ template.author || 'Unknown' }}</p>
            <div class="template-meta">
              <span class="gm-chip gm-chip--muted meta-tag">{{ template.grid_columns }}×{{ template.grid_rows }}</span>
              <span class="gm-chip meta-tag difficulty" :class="`difficulty-${template.difficulty_rating}`">
                {{ template.difficulty_rating }}
              </span>
              <span class="gm-chip meta-tag category">{{ template.category }}</span>
            </div>
            <p v-if="template.description" class="template-description">
              {{ template.description }}
            </p>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="gm-empty empty-state">
        <p class="gm-empty__title empty-message">No maps found</p>
        <p class="gm-empty__hint empty-hint">Try adjusting your filters or import a new map</p>
      </div>

      <!-- Action Buttons -->
      <div class="modal-actions">
        <button class="gm-btn gm-btn--ghost" @click="$emit('close')">Cancel</button>
        <button
          class="gm-btn gm-btn--primary"
          :disabled="!selectedTemplate"
          @click="handleLoadTemplate"
        >
          Load Map
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import battleMapService from '@/services/battleMapService'

export default {
  name: 'BattleMapLibrary',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'load-template'],
  data() {
    return {
      templates: [],
      loading: false,
      error: null,
      searchQuery: '',
      selectedCategory: '',
      selectedDifficulty: '',
      selectedTemplate: null,
      searchDebounce: null
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.loadTemplates()
      }
    }
  },
  methods: {
    async loadTemplates() {
      this.loading = true
      this.error = null

      try {
        const filters = {
          search: this.searchQuery || undefined,
          category: this.selectedCategory || undefined,
          difficulty: this.selectedDifficulty || undefined
          // Don't filter by published - GMs should see all templates
        }

        this.templates = await battleMapService.listTemplates(filters)
        console.log('[BattleMapLibrary] Loaded templates:', this.templates.length)
      } catch (err) {
        console.error('[BattleMapLibrary] Error loading templates:', err)
        this.error = err.message || 'Failed to load templates'
      } finally {
        this.loading = false
      }
    },

    handleSearchChange() {
      // Debounce search
      clearTimeout(this.searchDebounce)
      this.searchDebounce = setTimeout(() => {
        this.loadTemplates()
      }, 300)
    },

    selectTemplate(template) {
      this.selectedTemplate = template
    },

    async handleLoadTemplate() {
      if (!this.selectedTemplate) return

      try {
        this.loading = true
        this.$emit('load-template', this.selectedTemplate.id)
        this.$emit('close')
      } catch (err) {
        console.error('[BattleMapLibrary] Error loading template:', err)
        this.error = 'Failed to load template'
      } finally {
        this.loading = false
      }
    },

    async handleDeleteTemplate(template) {
      try {
        console.log('[BattleMapLibrary] Deleting template:', template.id)
        await battleMapService.deleteTemplate(template.id)

        // Remove from local list
        this.templates = this.templates.filter(t => t.id !== template.id)

        // Clear selection if deleted template was selected
        if (this.selectedTemplate?.id === template.id) {
          this.selectedTemplate = null
        }

        console.log('[BattleMapLibrary] Template deleted successfully')
      } catch (err) {
        console.error('[BattleMapLibrary] Error deleting template:', err)
        this.error = 'Failed to delete template: ' + err.message
      }
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
}

.library-modal {
  width: 90%;
  max-width: 75rem;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: var(--gm-gap);
  margin-bottom: var(--gm-gap);
  border-bottom: 1px solid var(--gm-border-dim);
}

.modal-title {
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  color: var(--gm-text-muted);
  font-size: 1.75rem;
  cursor: pointer;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--gm-radius-sm);
  transition: color 0.15s ease, background 0.15s ease;
}

.close-btn:hover {
  background: var(--gm-accent-soft);
  color: var(--gm-accent);
}

.filters-section {
  display: flex;
  gap: var(--gm-gap-sm);
  padding-bottom: var(--gm-gap);
  margin-bottom: var(--gm-gap);
  border-bottom: 1px solid var(--gm-border-dim);
}

.search-input {
  flex: 1;
}

.filter-select {
  min-width: 9.5rem;
}

.loading-state,
.error-state,
.empty-state {
  flex: 1;
}

.error-message {
  color: var(--gm-danger);
  margin-bottom: var(--gm-gap);
}

.templates-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(17.5rem, 1fr));
  gap: var(--gm-gap-lg);
  padding-bottom: var(--gm-gap);
  flex: 1;
}

.template-card {
  background: var(--gm-surface-2);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius);
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.15s ease;
}

.template-card:hover {
  border-color: var(--gm-border);
}

.template-card.selected {
  border-color: var(--gm-border-hi);
}

.template-thumbnail {
  width: 100%;
  height: 10rem;
  overflow: hidden;
  background: var(--gm-surface-sunken);
  display: flex;
  align-items: center;
  justify-content: center;
}

.thumbnail-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-placeholder {
  font-family: var(--gm-font-display);
  font-size: 0.85rem;
  letter-spacing: 0.14em;
  color: var(--gm-text-dim);
}

.template-info {
  padding: var(--gm-gap);
}

.template-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--gm-gap-sm);
  margin-bottom: 0.3rem;
}

.template-name {
  margin: 0;
  font-size: 0.95rem;
  font-weight: bold;
  color: var(--gm-text);
  flex: 1;
}

.template-author {
  margin: 0 0 var(--gm-gap-sm);
  font-size: 0.7rem;
  color: var(--gm-text-muted);
  font-style: italic;
}

.template-meta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--gm-gap-xs);
  margin-bottom: var(--gm-gap-sm);
}

.meta-tag.difficulty-easy { color: var(--gm-success); }
.meta-tag.difficulty-medium { color: var(--gm-accent-hi); }
.meta-tag.difficulty-hard { color: var(--gm-danger); }

.template-description {
  margin: 0;
  font-size: 0.78rem;
  color: var(--gm-text-muted);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.empty-state {
  text-align: center;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--gm-gap-sm);
  padding-top: var(--gm-gap);
  margin-top: var(--gm-gap);
  border-top: 1px solid var(--gm-border-dim);
}
</style>
